import { Component, ElementRef, ViewChild, effect, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatCheckbox } from "@angular/material/checkbox";
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { DiagnosisEventService } from '../../services/diagnosis-event/diagnosis-event.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OkDialogComponent, OkDialogModel } from '../ok-dialog/ok-dialog.component';
import { ExportAsmPocRequest } from '../../models/export-asm-poc-request';

export interface AsmFileGenerationAndSubmissionModel {
  recordCount: number;
  userFirstInitial: string;
  userLastName: string;
  exportedDateTime: string;
  payerID: number;
  serviceYear: number;
  templateID: number;
}

export const ASM_FILE_GEN_AND_SUB_CONFIG = {
  panelClass: 'dialog-container',
  autoFocus: false,
  width: '60dvw',
  maxHeight: '80dvw',
  disableClose: true
};

@Component({
    selector: 'app-asm-file-generation-and-submission',
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatIcon,
        MatCheckbox,
        MatTooltipModule,
        MatFormField,
        MatLabel,
        ReactiveFormsModule,
        FormsModule,
        MatInput,
        MatDialogActions,
        MatButton
    ],
    templateUrl: './asm-file-generation-and-submission.component.html',
    styleUrl: './asm-file-generation-and-submission.component.scss'
})
export class AsmFileGenerationAndSubmissionComponent implements OnInit {
  public data = inject(MAT_DIALOG_DATA) as AsmFileGenerationAndSubmissionModel;
  public readonly tooltipText =
    'When selected, exported records are set to ASM Submitted. ' +
    'Deselect to leave the statuses unchanged.';

  // IMPORTANT: include a trailing newline/space so user typing starts cleanly
  private readonly defaultNote: string;

  checkboxSignal = signal<boolean>(true);

  asmForm: FormGroup = inject(FormBuilder).group({});
  notesControl = new FormControl<string>('', { nonNullable: true });
  notesControlName = 'asm-notes-field';

  @ViewChild('notesArea', { static: true }) notesArea!: ElementRef<HTMLTextAreaElement>;

  dxService = inject(DiagnosisEventService);

  constructor(
    public dialogRef: MatDialogRef<AsmFileGenerationAndSubmissionComponent>,
    private dialog: MatDialog,
  ) {
    this.defaultNote =
      `ASM Submitted ${this.data.userFirstInitial}${this.data.userLastName} ${this.data.exportedDateTime}\n`;

    // React to the checkbox signal: apply/remove immutable prefix
    effect(() => {
      if (this.checkboxSignal()) {
        this.applyDefaultIfNeeded();
      } else {
        this.removeDefaultIfPresent();
      }
    });
  }

  ngOnInit(): void {
    this.asmForm.addControl(this.notesControlName, this.notesControl);
  }

  handleClose(): void {
    this.dialogRef.close(null);
  }

  // ---------- Immutable-prefix helpers ----------

  private get prefixLen() { return this.defaultNote.length; }

  /** Add prefix if missing and place caret after it */
  private applyDefaultIfNeeded() {
    const v = this.notesControl.value;
    if (!v.startsWith(this.defaultNote)) {
      this.notesControl.setValue(this.defaultNote + v, { emitEvent: false });
      queueMicrotask(() => this.placeCaretAtPrefixEnd());
    }
  }

  /** Remove prefix if present when the box is unchecked */
  private removeDefaultIfPresent() {
    const v = this.notesControl.value;
    if (v.startsWith(this.defaultNote)) {
      this.notesControl.setValue(v.substring(this.prefixLen), { emitEvent: false });
      // leave caret where it is naturally
    }
  }

  /** Keep caret from entering the prefix; block deleting any part of it */
  blockEditingPrefix(e: KeyboardEvent) {
    if (!this.checkboxSignal()) return;

    const el = this.notesArea.nativeElement;
    const p  = this.prefixLen;
    const start = el.selectionStart ?? 0;
    const end   = el.selectionEnd ?? 0;
    const hasSel = start !== end;
    const key = e.key;

    // keep nav out of prefix
    if ((key === 'ArrowLeft' || key === 'Home' || key === 'PageUp') && start <= p) {
      e.preventDefault();
      this.placeCaretAtPrefixEnd();
      return;
    }

    // --- BACKSPACE ---
    if (key === 'Backspace') {
      if (!hasSel && start <= p) { e.preventDefault(); this.placeCaretAtPrefixEnd(); return; }
      if (hasSel && end <= p)     { e.preventDefault(); this.placeCaretAtPrefixEnd(); return; }
      if (start < p && end > p)   { e.preventDefault(); this.deleteClamped(p, end); return; }
      return; // fully in user text
    }

    // --- DELETE ---
    if (key === 'Delete') {
      // No selection
      if (!hasSel) {
        if (start < p) {                // caret inside prefix -> delete first user char
          e.preventDefault();
          this.deleteOneAtBoundary();   // deletes [p, p+1)
          return;
        }
        return; // caret >= p -> allow native delete
      }

      // Selection present
      // Entirely in prefix -> block
      if (end <= p) {
        e.preventDefault();
        this.placeCaretAtPrefixEnd();
        return;
      }

      // Overlaps or fully in user text -> delete only user portion
      e.preventDefault();
      this.deleteClamped(start, end);   // clamps to [max(start,p), max(end,p))
      return;
    }
  }


  /** Delete only the range after the prefix; clamps [from,to) to never touch the prefix. */
  private deleteClamped(from: number, to: number) {
    const p = this.prefixLen;
    const v = this.notesControl.value;
    const s = Math.max(from, p);
    const e = Math.max(to,   p);

    if (s >= e) { this.placeCaretAtPrefixEnd(); return; }

    const next = v.slice(0, s) + v.slice(e);
    this.notesControl.setValue(next, { emitEvent: false });

    queueMicrotask(() => {
      const el = this.notesArea.nativeElement;
      el.setSelectionRange(s, s);
      el.focus();
    });
  }

  /** Pressing Delete while caret is in the prefix deletes the first user character, if any. */
  private deleteOneAtBoundary() {
    const p = this.prefixLen;
    const v = this.notesControl.value;

    if (v.length <= p) {               // nothing to delete
      this.placeCaretAtPrefixEnd();
      return;
    }

    const next = v.slice(0, p) + v.slice(p + 1);
    this.notesControl.setValue(next, { emitEvent: false });

    queueMicrotask(() => {
      const el = this.notesArea.nativeElement;
      el.setSelectionRange(p, p);
      el.focus();
    });
  }

  /** Ensure the control value always starts with the prefix while active */
  enforcePrefix() {
    if (!this.checkboxSignal()) return;

    const v = this.notesControl.value;
    if (!v.startsWith(this.defaultNote)) {
      const userPart = v.slice(Math.min(v.length, this.prefixLen));
      this.notesControl.setValue(this.defaultNote + userPart, { emitEvent: false });
      this.snapCaret();
    }
  }

  /** If user clicks/taps inside prefix, snap caret to prefix end */
  snapCaret() {
    if (!this.checkboxSignal()) return;

    const el = this.notesArea.nativeElement;
    const s = el.selectionStart ?? 0;
    if (s < this.prefixLen) this.placeCaretAtPrefixEnd();
  }

  private placeCaretAtPrefixEnd() {
    const el = this.notesArea.nativeElement;
    const pos = this.prefixLen;
    el.setSelectionRange(pos, pos);
    el.focus();
  }

  // Optional: call this when collecting the user's free text (without prefix)
  get userNotesOnly(): string {
    const v = this.notesControl.value;
    return this.checkboxSignal() && v.startsWith(this.defaultNote)
      ? v.substring(this.prefixLen)
      : v;
  }

  export() {
    const notes: string[] = [];
    if (this.checkboxSignal()) {
      notes.push(this.defaultNote.trim());
    }
    notes.push(this.userNotesOnly.trim());
    this.dialogRef.close({ toUpdateStatus: this.checkboxSignal(), notes: notes });
  }
}